import React, { useState, useEffect, useCallback } from 'react';
import { RouteAnalysis } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

interface RoutePointsSummaryProps {
  analysis: RouteAnalysis;
}

interface RoutePointWithAddress {
  latitude: number;
  longitude: number;
  timestamp?: string;
  address?: string;
  loading?: boolean;
}

const RoutePointsSummary: React.FC<RoutePointsSummaryProps> = ({ analysis }) => {
  const { t } = useLanguage();
  const [selectedRoute, setSelectedRoute] = useState<'actual' | 'optimal'>('actual');
  const [pointsWithAddresses, setPointsWithAddresses] = useState<RoutePointWithAddress[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  const actualPoints = analysis.actualRoute?.routePoints || [];
  const optimalPoints = analysis.optimalRoute?.routePoints || [];

  // Filter points to only show those 30 minutes apart
  const filterPointsByTime = (points: any[], minMinutes: number = 30): any[] => {
    if (points.length === 0) return [];
    
    // For optimal route (no timestamps), take every Nth point
    if (!points[0].timestamp) {
const interval = Math.max(1, Math.floor(points.length / 20)); // Max 20 points
      return points.filter((_, index) => index % interval === 0 || index === points.length - 1);
    }

    // For actual route with timestamps
    const filtered: any[] = [points[0]]; // Always include first point
    let lastTime = new Date(points[0].timestamp).getTime();

    for (let i = 1; i < points.length; i++) {
      const currentTime = new Date(points[i].timestamp).getTime();
    const minutesDiff = (currentTime - lastTime) / (1000 * 60);

      if (minutesDiff >= minMinutes) {
      filtered.push(points[i]);
        lastTime = currentTime;
      }
    }

    // Always include last point if not already included
    if (filtered[filtered.length - 1] !== points[points.length - 1]) {
      filtered.push(points[points.length - 1]);
    }

    return filtered;
  };

  // Get filtered points
  const getFilteredPoints = () => {
    const points = selectedRoute === 'actual' ? actualPoints : optimalPoints;
    return filterPointsByTime(points, 30);
  };

  // Load addresses when route changes
  const loadAddresses = useCallback(async () => {
    const allPoints = selectedRoute === 'actual' ? actualPoints : optimalPoints;
    const filteredPoints = filterPointsByTime(allPoints, 30);
    
    // Initialize points without addresses
    setPointsWithAddresses(filteredPoints.map(p => ({
      latitude: p.latitude,
      longitude: p.longitude,
      timestamp: p.timestamp,
      address: undefined,
      loading: true
    })));
    setLoadingAddresses(true);

    try {
      // Use batch geocoding API for better performance
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const pointsToResolve = filteredPoints.slice(0, 30); // Max 30 points

      const batchResponse = await axios.post(`${API_BASE_URL}/geocoding/reverse/batch`, {
        coordinates: pointsToResolve.map(p => ({
          latitude: p.latitude,
          longitude: p.longitude
    }))
      }, {
        timeout: 30000 // 30 second timeout for batch request
      });

      if (batchResponse.data && Array.isArray(batchResponse.data)) {
        setPointsWithAddresses(prev => {
          const updated = [...prev];
   batchResponse.data.forEach((result: any, index: number) => {
  if (updated[index]) {
         updated[index] = {
             ...updated[index],
        address: result.address || 'Address not found',
 loading: false
          };
            }
        });
          return updated;
        });
      }

  // Mark remaining points as not loaded (if any)
      if (filteredPoints.length > 30) {
      setPointsWithAddresses(prev => {
          const updated = [...prev];
          for (let i = 30; i < updated.length; i++) {
       if (updated[i]) {
           updated[i] = {
    ...updated[i],
       address: 'Click Export to load all',
             loading: false
         };
          }
        }
    return updated;
        });
      }
    } catch (error) {
      console.error('Error loading addresses in batch, falling back to individual requests:', error);
      
      // Fallback to individual requests if batch fails
      const batchSize = 5; // Increased from 3 since backend is faster
 const delayBetweenBatches = 500; // Reduced from 1000ms
      const pointsToResolve = filteredPoints.slice(0, 30);

      for (let i = 0; i < pointsToResolve.length; i += batchSize) {
        const batch = pointsToResolve.slice(i, Math.min(i + batchSize, pointsToResolve.length));
 
        const batchPromises = batch.map((point, batchIndex) => {
          const globalIndex = i + batchIndex;
          return getAddressFromCoordinates(point.latitude, point.longitude)
            .then(address => ({ globalIndex, address, success: true }))
            .catch(error => {
console.error(`Error loading address for point ${globalIndex}:`, error);
      return { globalIndex, address: 'Error loading address', success: false };
            });
        });

   const results = await Promise.all(batchPromises);
        
setPointsWithAddresses(prev => {
  const updated = [...prev];
          results.forEach(result => {
        if (updated[result.globalIndex]) {
 updated[result.globalIndex] = {
          ...updated[result.globalIndex],
      address: result.address,
       loading: false
   };
            }
          });
          return updated;
        });

    if (i + batchSize < pointsToResolve.length) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
        }
      }

      // Mark remaining points
      if (filteredPoints.length > 30) {
        setPointsWithAddresses(prev => {
const updated = [...prev];
          for (let i = 30; i < updated.length; i++) {
if (updated[i]) {
  updated[i] = {
...updated[i],
          address: 'Click Export to load all',
           loading: false
     };
          }
          }
          return updated;
        });
 }
    }

    setLoadingAddresses(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute, actualPoints, optimalPoints]); // getAddressFromCoordinates is stable and doesn't need to be a dependency

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const getAddressFromCoordinates = async (lat: number, lon: number): Promise<string> => {
    try {
      // Use backend API for reverse geocoding (uses local OSM data when available)
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const url = `${API_BASE_URL}/geocoding/reverse?lat=${lat}&lon=${lon}`;
  
      const response = await axios.get(url, {
        timeout: 10000 // 10 second timeout
      });

      if (response.data && response.data.address) {
        return response.data.address;
      }
      
      return 'Address not found';
    } catch (error) {
      console.error('Error fetching address:', error);
      return 'Error loading';
    }
  };

  // Format time in readable format - convert from UTC to local time
  const formatTime = (timestamp: string) => {
    // The timestamp from backend is in UTC format without 'Z' suffix
    // Append 'Z' to explicitly mark it as UTC, then convert to local time
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const date = new Date(utcTimestamp);
 
    // Now convert to Vietnam local time (GMT+7)
    return date.toLocaleTimeString('vi-VN', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'Asia/Ho_Chi_Minh' // Vietnam timezone (GMT+7)
    });
  };

  // Format date in readable format
  const formatDate = (timestamp: string) => {
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
    const date = new Date(utcTimestamp);
    
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      timeZone: 'Asia/Ho_Chi_Minh'
    });
  };

  // Get marker color based on position
  const getMarkerColor = (index: number, total: number) => {
    if (index === 0) return '#28a745'; // Green for start
    if (index === total - 1) return '#dc3545'; // Red for end
    return selectedRoute === 'actual' ? '#fd7e14' : '#6f42c1'; // Orange for actual, Purple for optimal
  };

  if (!analysis) return null;

  const filteredPointsCount = getFilteredPoints().length;

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-gradient" style={{ 
        background: selectedRoute === 'actual' 
          ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' 
   : 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
  color: 'white'
   }}>
        <div className="d-flex justify-content-between align-items-center">
<h6 className="mb-0">
  <i className="bi bi-geo-alt-fill me-2"></i>
          {t('route.points')}
        </h6>
   {loadingAddresses && (
          <div className="spinner-border spinner-border-sm" role="status">
<span className="visually-hidden">{t('loading')}</span>
            </div>
        )}
   </div>
    </div>

      <div className="card-body p-3">
        {/* Route selector */}
  <div className="btn-group mb-3 w-100 shadow-sm" role="group">
    <button
    type="button"
     className={`btn ${selectedRoute === 'actual' ? 'btn-danger' : 'btn-outline-danger'}`}
   onClick={() => setSelectedRoute('actual')}
      >
    <i className="bi bi-geo-fill me-2"></i>
          {t('actual.route')}
  <span className="badge bg-light text-danger ms-2">{filteredPointsCount}</span>
 </button>
   <button
 type="button"
     className={`btn ${selectedRoute === 'optimal' ? 'btn-success' : 'btn-outline-success'}`}
   onClick={() => setSelectedRoute('optimal')}
          >
        <i className="bi bi-map me-2"></i>
         {t('optimal.route')}
       <span className="badge bg-light text-success ms-2">{filteredPointsCount}</span>
     </button>
        </div>

        {/* Route statistics */}
   <div className="card bg-light border-0 mb-3">
          <div className="card-body p-3">
       <div className="row g-2">
    <div className="col-6">
   <small className="text-muted d-block">{t('filtered.points')}</small>
     <strong className="h6 mb-0">{filteredPointsCount}</strong>
      </div>
    <div className="col-6">
    <small className="text-muted d-block">{t('total.points')}</small>
     <strong className="h6 mb-0">{selectedRoute === 'actual' ? actualPoints.length : optimalPoints.length}</strong>
    </div>
  <div className="col-6">
         <small className="text-muted d-block">{t('distance')}</small>
            <strong className="h6 mb-0">
         {selectedRoute === 'actual' 
        ? analysis.actualRoute?.distanceKm.toFixed(2) 
  : analysis.optimalRoute?.distanceKm.toFixed(2)} km
     </strong>
  </div>
    <div className="col-6">
                <small className="text-muted d-block">{t('duration')}</small>
     <strong className="h6 mb-0">
      {selectedRoute === 'actual' 
      ? (analysis.actualRoute?.durationMinutes 
   ? `${analysis.actualRoute.durationMinutes.toFixed(0)} min`
              : 'N/A')
           : (analysis.optimalRoute?.durationMinutes 
    ? `${analysis.optimalRoute.durationMinutes.toFixed(0)} min`
   : 'N/A')}
     </strong>
      </div>
     </div>
     <hr className="my-2" />
      <small className="text-muted d-block">
         <i className="bi bi-info-circle me-1"></i>
   {t('showing.points.30min.apart')}
 </small>
    <small className="text-muted d-block">
      <i className="bi bi-speedometer2 me-1"></i>
    {t('loading.addresses')}
  </small>
   </div>
        </div>

        {/* Route points list - Adjust max height */}
      <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden' }}>
          {pointsWithAddresses.length > 0 ? (
         <div className="list-group list-group-flush">
        {pointsWithAddresses.map((point, index) => (
    <div 
                  key={index}
   className="list-group-item px-2 py-3 border-start-0 border-end-0"
          style={{ 
   borderLeft: `4px solid ${getMarkerColor(index, pointsWithAddresses.length)}`,
     transition: 'all 0.2s ease'
              }}
>
       {/* Point number and marker */}
     <div className="d-flex align-items-start mb-2">
                    <div 
  className="rounded-circle d-flex align-items-center justify-content-center me-2 fw-bold" 
   style={{ 
        minWidth: '28px',
    height: '28px',
          backgroundColor: getMarkerColor(index, pointsWithAddresses.length),
        color: 'white',
      fontSize: '0.75rem'
          }}
       >
                {index === 0 ? '🏁' : index === pointsWithAddresses.length - 1 ? '🎯' : index + 1}
            </div>
         <div className="flex-grow-1">
        {/* Position Label */}
    <div className="mb-1">
                 <span className="badge" style={{ 
     backgroundColor: getMarkerColor(index, pointsWithAddresses.length),
    fontSize: '0.7rem'
    }}>
      {index === 0 ? 'START' : index === pointsWithAddresses.length - 1 ? 'END' : `Point ${index + 1}`}
     </span>
</div>

{/* Address */}
           <div className="mb-2">
      {point.loading ? (
       <div className="d-flex align-items-center text-primary">
      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ width: '1rem', height: '1rem' }}>
            <span className="visually-hidden">Loading...</span>
     </div>
    <small className="fw-medium">{t('loading.address')}...</small>
  </div>
      ) : (
         <div className="d-flex align-items-start">
      <i className="bi bi-pin-map-fill me-2" style={{ fontSize: '1rem', marginTop: '2px', color: '#000000' }}></i>
    <div className="flex-grow-1">
 <div className="fw-medium" style={{ fontSize: '0.9rem', lineHeight: '1.4', color: '#000000' }}>
       {point.address || t('no.address.available')}
</div>
       </div>
   </div>
  )}
   </div>

     {/* Coordinates and Timestamp in same row */}
  <div className="d-flex flex-wrap align-items-center gap-3">
   {/* Coordinates */}
          <div className="d-flex align-items-center">
         <i className="bi bi-geo-alt me-2" style={{ fontSize: '0.9rem', color: '#000000' }}></i>
          <span className="font-monospace fw-medium" style={{ fontSize: '0.85rem', color: '#000000' }}>
     {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
  </span>
    </div>

{/* Timestamp (for actual route) */}
      {selectedRoute === 'actual' && point.timestamp && (
       <div className="d-flex align-items-center">
      <i className="bi bi-clock-fill me-2" style={{ fontSize: '0.9rem', color: '#000000' }}></i>
       <span className="fw-medium" style={{ fontSize: '0.85rem', color: '#000000' }}>
      {formatDate(point.timestamp)} {formatTime(point.timestamp)}
            </span>
         </div>
  )}
    </div>
           </div>
                  </div>
          </div>
   ))}
         </div>
        ) : (
  <div className="text-center py-5 text-muted">
          <i className="bi bi-geo d-block mb-3" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
              <p className="mb-0">{t('no.route.points.available')}</p>
            </div>
        )}
  </div>

        {/* Export button */}
     <div className="mt-3 d-grid">
    <button
    className="btn btn-outline-primary btn-sm shadow-sm"
            onClick={() => {
    const data = JSON.stringify(pointsWithAddresses, null, 2);
       const blob = new Blob([data], { type: 'application/json' });
const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
       a.href = url;
    a.download = `${selectedRoute}-route-${analysis.shippingRecordId}-filtered.json`;
     a.click();
  URL.revokeObjectURL(url);
       }}
      >
    <i className="bi bi-download me-2"></i>
       {t('export.route.points')}
  </button>
      </div>
      </div>
    </div>
  );
};

export default RoutePointsSummary;

